import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Icon,
  Input,
  LinearProgress,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import HttpHandler from 'app/reusable/HttpHandler'
import useRequest from 'app/hooks/useRequest'
import { useDismissModal } from 'app/ducks/modal/hooks'

const YesOrNoAnswer = ({ value, onChange }) => (
  <RadioGroup name="yes-or-no">
    <FormControlLabel
      control={<Radio checked={value === 'Sim'} />}
      label="Sim"
      onClick={() => onChange('Sim')}
    />
    <FormControlLabel
      control={<Radio checked={value === 'Não'} />}
      label="Não"
      onClick={() => onChange('Não')}
    />
  </RadioGroup>
)

const TextAnswer = ({ value, onChange }) => (
  <textarea
    rows={5}
    autoFocus
    className="w-full p-2"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}
  />
)

const NumberAnswer = ({ value, onChange }) => (
  <Input
    type="number"
    autoFocus
    className="w-full"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
  />
)

const SingleChoiceAnswer = ({ question, value, onChange }) => (
  <RadioGroup name="single-choice">
    {question.choices.map((choice) => (
      <div className="flex" key={choice.id}>
        <FormControlLabel
          control={<Radio checked={value?.choiceId === choice.id} />}
          label={choice.label}
          className="flex-grow"
          onClick={() =>
            onChange({
              choiceId: choice.id,
              label: choice.label,
              input: value?.input,
            })
          }
        />
        {choice.allow_input && value?.choiceId === choice.id && (
          <textarea
            rows={2}
            className="w-full p-2"
            value={value?.input || ''}
            onChange={(e) => onChange({ ...value, input: e.target.value })}
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              margin: '.5rem 0',
            }}
          />
        )}
      </div>
    ))}
  </RadioGroup>
)

const MultipleChoiceAnswer = ({ question, value, onChange }) => {
  const selected = value || {}

  return (
    <div>
      {question.choices.map((choice) => (
        <div className="flex" key={choice.id}>
          <FormControlLabel
            control={<Checkbox checked={!!selected[choice.id]} />}
            label={choice.label}
            className="flex-grow"
            onClick={() => {
              const next = { ...selected }
              if (next[choice.id]) delete next[choice.id]
              else next[choice.id] = { label: choice.label, input: null }
              onChange(next)
            }}
          />
          {choice.allow_input && selected[choice.id] && (
            <textarea
              rows={2}
              className="w-full p-2"
              value={selected[choice.id]?.input || ''}
              onChange={(e) =>
                onChange({
                  ...selected,
                  [choice.id]: {
                    ...selected[choice.id],
                    input: e.target.value,
                  },
                })
              }
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                margin: '.5rem 0',
              }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

const QUESTION_ANSWER_COMPONENTS = {
  yes_or_no: YesOrNoAnswer,
  text: TextAnswer,
  number: NumberAnswer,
  single_choice: SingleChoiceAnswer,
  multiple_choice: MultipleChoiceAnswer,
}

const flattenAnswers = (questions, answers) => {
  const payload = []

  for (const question of questions) {
    const value = answers[question.id]
    if (value === undefined || value === null || value === '') continue

    if (question.type === 'single_choice') {
      payload.push({
        question: question.id,
        value: value.label,
        choice: value.choiceId,
        input: value.input || null,
      })
    } else if (question.type === 'multiple_choice') {
      Object.entries(value).forEach(([choiceId, choiceValue]) => {
        payload.push({
          question: question.id,
          value: choiceValue.label,
          choice: Number(choiceId),
          input: choiceValue.input || null,
        })
      })
    } else {
      payload.push({ question: question.id, value: String(value) })
    }
  }

  return payload
}

const ThankYouScreen = ({ onClose }) => (
  <div className="text-center">
    <Icon style={{ fontSize: '4rem', color: '#4caf50' }}>check_circle</Icon>
    <h3 className="mt-3">Obrigado por responder!</h3>
    <p>Sua resposta foi registrada com sucesso.</p>
    <Button
      color="primary"
      variant="contained"
      size="large"
      className="w-full mt-4"
      onClick={onClose}
    >
      Fechar
    </Button>
  </div>
)

const SurveyFillModal = ({ surveyId }) => {
  const dismissModal = useDismissModal()
  const surveyRequest = useRequest(
    HttpHandler.getSurveyToFill,
    'surveyToFill',
    [surveyId]
  )
  const survey = surveyRequest.response?.data

  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFinished, setIsFinished] = useState(false)

  const questions = useMemo(() => survey?.questions || [], [survey])
  const currentQuestion = questions[step]
  const progress = questions.length
    ? Math.round((step / questions.length) * 100)
    : 0

  // The survey may already be answered (e.g. filled in another tab); bail
  // out of the modal instead of getting stuck on the loading spinner.
  useEffect(() => {
    if (surveyRequest.error) dismissModal()
  }, [surveyRequest.error, dismissModal])

  if (isFinished) {
    return <ThankYouScreen onClose={() => dismissModal()} />
  }

  if (surveyRequest.isLoading || !survey) {
    return (
      <>
        <LinearProgress />
        <p className="text-center my-4">Carregando...</p>
      </>
    )
  }

  const setAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }))
  }

  const handleFinish = () => {
    setIsSubmitting(true)
    toast
      .promise(
        HttpHandler.submitSurveyAnswers(
          surveyId,
          flattenAnswers(questions, answers)
        ),
        {
          pending: 'Enviando...',
          success: 'Respostas enviadas com sucesso!',
          error: 'Erro ao enviar respostas.',
        }
      )
      .then(() => setIsFinished(true))
      .finally(() => setIsSubmitting(false))
  }

  const AnswerComponent = QUESTION_ANSWER_COMPONENTS[currentQuestion?.type]

  return (
    <div>
      <LinearProgress variant="determinate" value={progress} className="mb-4" />
      <p className="text-muted mb-2">
        Pergunta {step + 1} de {questions.length}
      </p>
      <h4 className="mb-4">{currentQuestion?.title}</h4>

      {AnswerComponent && (
        <AnswerComponent
          question={currentQuestion}
          value={answers[currentQuestion.id]}
          onChange={setAnswer}
        />
      )}

      <div className="flex flex-between mt-6">
        <Button
          disabled={step === 0 || isSubmitting}
          onClick={() => setStep((prev) => prev - 1)}
        >
          Voltar
        </Button>
        {step < questions.length - 1 ? (
          <Button
            color="primary"
            variant="contained"
            onClick={() => setStep((prev) => prev + 1)}
          >
            Avançar
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            onClick={handleFinish}
          >
            Concluir
          </Button>
        )}
      </div>
    </div>
  )
}

export default SurveyFillModal
